<template>
  <div class="chat-file-uploader">
    <!-- File Drop Zone -->
    <div
      class="file-drop-zone"
      :class="{ 'is-dragging': isDragging, 'has-files': uploadedFiles.length > 0 }"
      @drop.prevent="handleDrop"
      @dragover.prevent="isDragging = true"
      @dragenter.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @click="triggerFileInput"
    >
      <input
        ref="fileInput"
        type="file"
        :multiple="multiple"
        :accept="acceptedFormats"
        @change="handleFileSelect"
        style="display: none"
      />

      <div class="drop-zone-content">
        <feather-icon
          :icon="isDragging ? 'DownloadCloudIcon' : 'UploadCloudIcon'"
          size="48"
          class="drop-zone-icon mb-1"
        />
        <h4 class="mb-50">{{ dropZoneTitle }}</h4>
        <p class="text-muted mb-0">
          {{ dropZoneDescription }}
        </p>
        <small class="text-muted d-block mt-50">
          Formats acceptés: {{ formatsDisplay }}
        </small>
      </div>
    </div>

    <!-- Upload Progress -->
    <b-progress
      v-if="uploadProgress > 0 && uploadProgress < 100"
      :value="uploadProgress"
      variant="primary"
      striped
      animated
      class="mt-1"
      height="8px"
    />

    <!-- Uploaded Files List -->
    <div v-if="uploadedFiles.length > 0" class="uploaded-files-list mt-2">
      <h5 class="mb-1">
        <feather-icon icon="FileIcon" size="18" class="mr-50" />
        Fichiers chargés ({{ uploadedFiles.length }})
      </h5>

      <b-list-group>
        <b-list-group-item
          v-for="file in uploadedFiles"
          :key="file.id || file.original_name"
          class="d-flex align-items-center justify-content-between"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              :variant="getFileVariant(file)"
              size="40"
              class="mr-1"
            >
              <feather-icon :icon="getFileIcon(file)" size="20" />
            </b-avatar>
            <div>
              <h6 class="mb-0">{{ file.original_name }}</h6>
              <small class="text-muted">{{ formatFileSize(file.size) }}</small>
            </div>
          </div>

          <div class="d-flex align-items-center">
            <b-badge
              v-if="file.status === 'uploading'"
              variant="light-warning"
              class="mr-50"
            >
              <feather-icon icon="LoaderIcon" size="14" class="spinner mr-25" />
              Envoi...
            </b-badge>
            <b-badge
              v-else-if="file.status === 'success'"
              variant="light-success"
              class="mr-50"
            >
              <feather-icon icon="CheckIcon" size="14" class="mr-25" />
              Envoyé
            </b-badge>
            <b-badge
              v-else-if="file.status === 'error'"
              variant="light-danger"
              class="mr-50"
            >
              <feather-icon icon="XIcon" size="14" class="mr-25" />
              Erreur
            </b-badge>

            <b-button
              v-if="!disabled"
              variant="flat-danger"
              size="sm"
              @click="removeFile(file)"
            >
              <feather-icon icon="Trash2Icon" size="16" />
            </b-button>
          </div>
        </b-list-group-item>
      </b-list-group>
    </div>

    <!-- Error Messages -->
    <b-alert
      v-if="errorMessage"
      variant="danger"
      class="mt-1"
      show
      dismissible
      @dismissed="errorMessage = null"
    >
      <div class="alert-body">
        <feather-icon icon="AlertCircleIcon" class="mr-50" />
        {{ errorMessage }}
      </div>
    </b-alert>
  </div>
</template>

<script>
import {
  BProgress,
  BListGroup,
  BListGroupItem,
  BAvatar,
  BBadge,
  BButton,
  BAlert
} from 'bootstrap-vue'

export default {
  name: 'ChatFileUploader',
  components: {
    BProgress,
    BListGroup,
    BListGroupItem,
    BAvatar,
    BBadge,
    BButton,
    BAlert
  },
  props: {
    chatType: {
      type: String,
      required: true,
      validator: val => ['rapport', 'cv'].includes(val)
    },
    multiple: {
      type: Boolean,
      default: true
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024 // 10MB
    },
    disabled: {
      type: Boolean,
      default: false
    },
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isDragging: false,
      uploadedFiles: this.value || [],
      uploadProgress: 0,
      errorMessage: null
    }
  },
  computed: {
    acceptedFormats() {
      if (this.chatType === 'rapport') {
        return '.pdf,.doc,.docx,.xls,.xlsx'
      } else if (this.chatType === 'cv') {
        return '.pdf,.doc,.docx'
      }
      return '*'
    },
    formatsDisplay() {
      if (this.chatType === 'rapport') {
        return 'PDF, Word, Excel'
      } else if (this.chatType === 'cv') {
        return 'PDF, Word'
      }
      return 'Tous formats'
    },
    dropZoneTitle() {
      return this.isDragging
        ? 'Déposez vos fichiers ici'
        : 'Glissez-déposez vos fichiers ou cliquez pour sélectionner'
    },
    dropZoneDescription() {
      if (this.chatType === 'rapport') {
        return 'Chargez un ou plusieurs fichiers pour générer votre rapport'
      } else if (this.chatType === 'cv') {
        return 'Chargez votre modèle de CV'
      }
      return 'Chargez vos fichiers'
    }
  },
  watch: {
    value(newVal) {
      this.uploadedFiles = newVal || []
    },
    uploadedFiles: {
      handler(newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    }
  },
  methods: {
    triggerFileInput() {
      if (!this.disabled) {
        this.$refs.fileInput.click()
      }
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      this.processFiles(files)
      // Reset input
      event.target.value = ''
    },
    handleDrop(event) {
      this.isDragging = false
      if (this.disabled) return

      const files = Array.from(event.dataTransfer.files)
      this.processFiles(files)
    },
    processFiles(files) {
      this.errorMessage = null

      // Validate files
      const validFiles = []
      for (const file of files) {
        // Check file size
        if (file.size > this.maxFileSize) {
          this.errorMessage = `Le fichier "${file.name}" dépasse la taille maximale de ${this.formatFileSize(this.maxFileSize)}`
          continue
        }

        // Check file type
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
        if (!this.acceptedFormats.includes(fileExtension)) {
          this.errorMessage = `Le format du fichier "${file.name}" n'est pas accepté`
          continue
        }

        validFiles.push({
          id: `file-${Date.now()}-${Math.random()}`,
          name: file.name,
          size: file.size,
          type: file.type,
          file: file,
          status: 'pending'
        })
      }

      // Add to uploaded files
      if (!this.multiple && validFiles.length > 0) {
        this.uploadedFiles = [validFiles[0]]
      } else {
        this.uploadedFiles = [...this.uploadedFiles, ...validFiles]
      }

      // Emit upload event
      if (validFiles.length > 0) {
        this.$emit('upload', validFiles)
      }
    },
    removeFile(file) {
      const index = this.uploadedFiles.findIndex(f => f.id === file.id)
      if (index > -1) {
        this.uploadedFiles.splice(index, 1)
        this.$emit('remove', file)
      }
    },
    updateFileStatus(fileId, status) {
      const file = this.uploadedFiles.find(f => f.id === fileId)
      if (file) {
        file.status = status
      }
    },
    setUploadProgress(progress) {
      this.uploadProgress = progress
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },
    getFileIcon(file) {
      const extension = file.original_name.split('.').pop().toLowerCase()
      if (['pdf'].includes(extension)) return 'FileTextIcon'
      if (['doc', 'docx'].includes(extension)) return 'FileIcon'
      if (['xls', 'xlsx'].includes(extension)) return 'GridIcon'
      return 'FileIcon'
    },
    getFileVariant(file) {
      const extension = file.original_name.split('.').pop().toLowerCase()
      if (['pdf'].includes(extension)) return 'light-danger'
      if (['doc', 'docx'].includes(extension)) return 'light-primary'
      if (['xls', 'xlsx'].includes(extension)) return 'light-success'
      return 'light-secondary'
    }
  }
}
</script>

<style lang="scss" scoped>
.file-drop-zone {
  border: 2px dashed #d8d6de;
  border-radius: 8px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #f8f8f8;

  &:hover {
    border-color: var(--primary);
    background-color: rgba(115, 103, 240, 0.05);
  }

  &.is-dragging {
    border-color: var(--primary);
    background-color: rgba(115, 103, 240, 0.1);
    transform: scale(1.02);

    .drop-zone-icon {
      animation: bounce 0.5s infinite;
    }
  }

  &.has-files {
    padding: 2rem 1.5rem;
  }
}

.drop-zone-icon {
  color: var(--primary);
}

.uploaded-files-list {
  .list-group-item {
    border-left: 3px solid var(--primary);
  }
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}
</style>
